// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSimsStore from '@/app/store/modules/sims';
import actionsSims from '../../../api/actionsSims';
// Components
import Confirmation from './components/Confirmation';
import NonActivatedList from './components/NonActivatedList';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import SimCardsTable from '../../tables/SimCardsTable';
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import SelectProviderAndOffer from '../../forms/SelectProviderAndOffer';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import simCardFields from '../../../fields/simCardFields';
import SIM_OFFERS_PROVIDERS from '../../../enums/SimOffersProviders';
import ORGANIZATION_TYPES from '@/app/modules/organizations/enums/OrganizationTypes';
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalClientSimsMultiActivation
 */
function ModalClientSimsMultiActivation({ isOpen, setIsOpen }) {
    // Constants
    const STEPS_NUMBER = 4;
    const STEP_TITLE_PREFIX = 'modal.multiple_activation.steps';

    // Toast
    const { toast } = useToast();

    // Store
    const { rootOrganization } = useRootOrganizationStore();
    const storeRefetchSimCards = useSimsStore(s => s.storeRefetchSimCards);

    // State
    const [step, setStep] = useState(0);
    const [showStepTitle, setShowStepTitle] = useState(true);
    const [selectedSimCards, setSelectedSimCards] = useState(getInitialValueForRowSelected());
    const [selectedClient, setSelectedClient] = useState(getInitialValueForRowSelected());
    const [selectedProvider, setSelectedProvider] = useState('');
    const [selectedOffer, setSelectedOffer] = useState('');
    const [selectedOfferName, setSelectedOfferName] = useState('');
    const [nonActivatedSims, setNonActivatedSims] = useState([]);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.provider_selection`, disabledNext: !selectedProvider || !selectedOffer },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.client_selection`, disabledNext: !Object.keys(selectedClient.rowIds).length },
            2: { stepTitle: `${STEP_TITLE_PREFIX}.sims_selection`, disabledNext: !Object.keys(selectedSimCards.rowIds).length },
            3: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [selectedSimCards, selectedClient, selectedProvider, selectedOffer]
    );

    // Api call
    // Submit action
    const onSuccess = (data) => {
        if (data.length) {
            setShowStepTitle(false);
            setNonActivatedSims(data);
        }
        else {
            storeRefetchSimCards(true);
            setIsOpen(false);
            toast({
                description: <Entity entity='modal.multiple_activation.success' namespace={nsSimCard} />,
                variant: 'success'
            });
        }
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.multiple_activation.error' namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitMultiActivateSimCards = actionsSims.multiActivateSimCards(onSuccess, onError);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 1 || step === 2 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title='modal.multiple_activation.title' namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    {
                        showStepTitle &&
                        <Entity
                            entity={stepTitle}
                            data={{
                                step: step + 1,
                                maxStep: STEPS_NUMBER
                            }}
                            namespace={nsSimCard}
                            className='mb-2 flex'
                        />
                    }
                    {
                        step === 0 &&
                        <SelectProviderAndOffer
                            organizationId={rootOrganization.uuid}
                            selectedProvider={selectedProvider}
                            setSelectedProvider={setSelectedProvider}
                            selectedOffer={selectedOffer}
                            setSelectedOffer={setSelectedOffer}
                            setSelectedOfferName={setSelectedOfferName}
                            isOfferActive={true}
                        />
                    }
                    {
                        step === 1 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: organizationFields.TYPE.key,
                                        value: ORGANIZATION_TYPES.WHITE_LABEL.name,
                                        hidden: true
                                    }
                                ]
                            }}
                            rowsSelected={selectedClient}
                            setRowsSelected={setSelectedClient}
                            singleSelection={true}
                        />
                    }
                    {
                        step === 2 &&
                        <SimCardsTable
                            filtersEventName={setFiltersEventName('sim_cards', true)}
                            rowsSelected={selectedSimCards}
                            setRowsSelected={setSelectedSimCards}
                            customQuery={{
                                filters: [
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.SIM_CARD.STATUS),
                                        value: 'ACTIVABLE',
                                        hidden: true
                                    },
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.CLIENT.UUID),
                                        value: rootOrganization.uuid,
                                        hidden: true
                                    },
                                    {
                                        key: generateNestedFieldAccessor(simCardFields.SIM_CARD.PROVIDER),
                                        value: Object.values(SIM_OFFERS_PROVIDERS).find((item) => item.key === selectedProvider)?.name,
                                        hidden: true
                                    }
                                ]
                            }}
                        />
                    }
                    {
                        step === 3 &&
                        <>
                            {
                                nonActivatedSims.length === 0 ?
                                    <Confirmation selectedOffer={selectedOfferName} />
                                    :
                                    <NonActivatedList nonActivatedSims={nonActivatedSims} />
                            }
                        </>
                    }
                </ModalBody>

                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={() => {
                        submitMultiActivateSimCards.mutate({
                            simCardPlan: selectedOffer,
                            simCardIds: selectedSimCards.rowsData.map((item) => item.sim.uuid)
                        });
                    }}
                    isLoading={submitMultiActivateSimCards.isLoading}
                    disableValidation={disabledNext}
                    isError={nonActivatedSims.length > 0}
                />

            </ModalContent>
        </Modal>
    );
}

ModalClientSimsMultiActivation.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalClientSimsMultiActivation;